/**
 * Copyright (C) 2017 HERE Global B.V. and its affiliate(s).
 * All rights reserved.
 *
 * This software and other materials contain proprietary information
 * controlled by HERE and are protected by applicable copyright legislation.
 * Any use and utilization of this software and other materials and
 * disclosure to any third parties is conditional upon having a separate
 * agreement with HERE for the access, use, utilization or disclosure of this
 * software. In the absence of such agreement, the use of the software is not
 * allowed.
 */

import { LuiBase, LuiPositioningMixin } from '@here/lui-base'
import scopedCss from 'lui-tabbar.scss'
import template from 'lui-tabbar.html'

/**
 * LUI Tabbar
 */
export default class LuiTabbar extends LuiPositioningMixin(LuiBase) {
  static get is() {
    return 'lui-tabbar'
  }

  static get cssClassToggles() {
    return ['vertical']
  }

  static get css() {
    return scopedCss
  }

  static get template() {
    return template
  }

  static get hasLuiElementsInShadowDom() {
    return false
  }

  onConnected() {
    this._onChange = this._onChange || ((event) => {
      if (event.target.selected) {
        this._updateSelection(event.target)
      } else {
        this._initializeTabHighlightPosition(event.target)
      }
    })
    if (window.lui.usePolyfills) {
      requestAnimationFrame(() => {
        this.addEventListener('change', this._onChange)
      })
    } else {
      this.addEventListener('change', this._onChange)
    }
  }

  onDisconnected() {
    this.removeEventListener('change', this._onChange)
  }

  init() {
    this.noInitialTabSelection = !this.querySelector('lui-tab[selected]')

    // reschedule tab selection
    requestAnimationFrame(() => {
      this._updateSelection(this.selectedTab())
    })
  }

  _updateSelection(tab) {
    this.setTabHighlight(tab)
    this._deselectLastTab(tab)
  }

  _deselectLastTab(currentSelectedTab) {
    this.allTabs().forEach((tab) => {
      if (currentSelectedTab !== tab) {
        tab.selected = false
      }
    })
  }

  setTabHighlight(selectedTab) {
    if (!selectedTab) return
    if (this.noInitialTabSelection) {
      // Animation from center of tab:
      this.withoutTransition(() => {
        this._initializeTabHighlightPosition(selectedTab)
      }).then(() => {
        requestAnimationFrame(() => this._updateTabHighlightPosition(selectedTab))
      })
      this.noInitialTabSelection = false
    } else if (window.lui.usePolyfills && this.querySelector('lui-tab[manual], lui-tab[semimanual]')) {
      // Turn off transitions for polyfills:
      this.withoutTransition(() => {
        this._updateTabHighlightPosition(selectedTab)
      })
    } else {
      // Select with animation from last position:
      requestAnimationFrame(() => this._updateTabHighlightPosition(selectedTab))
    }
  }

  _initializeTabHighlightPosition(selectedTab) {
    const relativePosition = this.getRelativePosition(selectedTab)
    const rect = selectedTab.getBoundingClientRect()
    const markerEndsMargin = parseInt(window.getComputedStyle(this).getPropertyValue('--lui-tabbar-selected-marker-ends-margin'), 10) || 0

    this.style.setProperty('--lui-tabbar-selected-top', (relativePosition.top + rect.height / 2 + markerEndsMargin) + 'px')
    this.style.setProperty('--lui-tabbar-selected-left', (relativePosition.left + rect.width / 2 + markerEndsMargin) + 'px')
    this.style.setProperty('--lui-tabbar-selected-width', 0)
    this.style.setProperty('--lui-tabbar-selected-height', 0)
  }

  _updateTabHighlightPosition(selectedTab) {
    const relativePosition = this.getRelativePosition(selectedTab)
    const rect = selectedTab.getBoundingClientRect()
    const markerEndsMargin = parseInt(window.getComputedStyle(this).getPropertyValue('--lui-tabbar-selected-marker-ends-margin'), 10) || 0

    this.style.setProperty('--lui-tabbar-selected-top', relativePosition.top + markerEndsMargin + 'px')
    this.style.setProperty('--lui-tabbar-selected-left', relativePosition.left + markerEndsMargin + 'px')
    this.style.setProperty('--lui-tabbar-selected-width', rect.width - 2 * markerEndsMargin + 'px')
    this.style.setProperty('--lui-tabbar-selected-height', rect.height - 2 * markerEndsMargin + 'px')
  }

  allTabs() {
    return Array.from(this.querySelectorAll('lui-tab'))
  }

  selectedTab() {
    return this.querySelector('lui-tab[selected]')
  }
}



//////////////////
// WEBPACK FOOTER
// ./packages/lui-tabbar/lui-tabbar.js
// module id = 338
// module chunks = 0 1 6